import { saveAs } from 'file-saver';
import React from 'react';
import { toast } from 'react-toastify';

import { HttpCodes } from '../enums/httpCodes';
import { HttpService, REQUEST_TIMEOUT } from '../services/http.service';

export const useDownload = () => {
  const [isDownloading, setIsDownloading] = React.useState<boolean>(false);

  const pollRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRejectRef = React.useRef<(() => void) | null>(null);

  const abortFunc = React.useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
    abortRejectRef.current?.();
    abortRejectRef.current = null;
    setIsDownloading(false);
  }, []);

  const saveFileDirectly = async (url: string, name: string) => {
    if (!url) {
      return;
    }

    setIsDownloading(true);
    await HttpService.fetchAuthorized(url, {
      method: 'GET',
    })
      .then((res) => {
        const file = new File([res], name);
        saveAs(file);

        toast.dismiss(name);
        toast.success(`File ${name} has been downloaded`, {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  // Poll until the file is ready, then return the blob. Throws on error/timeout.
  const pollForFile = async (url: string, name: string, withoutAuth: boolean, timeout: number): Promise<Blob> => {
    setIsDownloading(true);
    let toastShown = false;

    /* eslint-disable no-await-in-loop, no-constant-condition, no-continue */
    while (true) {
      const result = await HttpService.downloadFile(url, withoutAuth);
      if (result === null) {
        throw new Error('aborted');
      }
      if (result === REQUEST_TIMEOUT) {
        throw new Error('timed out');
      }
      if (!(result instanceof Response)) {
        throw new Error('invalid response from downloadFile');
      }
      const res = result;

      if (res.status === HttpCodes.NOT_FOUND || res.status === HttpCodes.ACCEPTED) {
        if (!toastShown) {
          toast.info(`File ${name} is being created`, {
            position: 'top-center',
            autoClose: false,
            toastId: name,
          });
          toastShown = true;
        }

        await new Promise<void>((resolve, reject) => {
          pollRef.current = setTimeout(resolve, timeout);
          abortRejectRef.current = () => reject(new Error('aborted'));
        });
        abortRejectRef.current = null;

        continue;
      }

      if (res.status === HttpCodes.TIMEOUT) {
        throw new Error(`timed out`);
      }

      if (res.status === HttpCodes.SERVER_ERROR) {
        throw new Error(`server error`);
      }

      if (!res.ok) {
        throw new Error(`unexpected status ${res.status}`);
      }

      return res.blob();
    }
    /* eslint-enable no-await-in-loop, no-constant-condition, no-continue */
  };

  const checkIfFileExistsAndDownload = async (url: string, name: string, withoutAuth = false, timeout = 3000): Promise<boolean> => {
    try {
      const blob = await pollForFile(url, name, withoutAuth, timeout);
      saveAs(blob, name);

      toast.dismiss(name);
      toast.success(`File ${name} has been downloaded`, {
        position: 'top-center',
        autoClose: 4000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
      });

      return true;
    } catch (err: any) {
      toast.dismiss(name);
      if (err?.message !== 'aborted') {
        if (err?.message === 'timed out') {
          toast.error(`Download timed out for ${name}. Please try again.`);
        } else {
          toast.error(`There was an error creating file ${name}.`);
        }
      }

      return false;
    } finally {
      setIsDownloading(false);
      pollRef.current = null;
    }
  };

  const checkIfFileExistsAndPrint = async (url: string, name: string, withoutAuth = false, timeout = 3000): Promise<boolean> => {
    try {
      const blob = await pollForFile(url, name, withoutAuth, timeout);
      const blobUrl = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';

      const cleanupIframe = () => {
        if (iframe.parentNode) document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      };

      await new Promise<void>((resolve, reject) => {
        iframe.onload = () => {
          iframe.contentWindow?.print();
          setTimeout(cleanupIframe, 1000);
          resolve();
        };
        iframe.onerror = () => {
          cleanupIframe();
          reject(new Error(`Failed to load ${name} for printing`));
        };
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
      });

      toast.dismiss(name);

      return true;
    } catch (err: any) {
      toast.dismiss(name);
      if (err?.message !== 'aborted') {
        if (err?.message === 'timed out') {
          toast.error(`Print timed out for ${name}. Please try again.`);
        } else {
          toast.error(`There was an error creating file ${name}.`);
        }
      }

      return false;
    } finally {
      setIsDownloading(false);
      pollRef.current = null;
    }
  };

  return { isDownloading, setIsDownloading, checkIfFileExistsAndDownload, checkIfFileExistsAndPrint, abortFunc, saveFileDirectly };
};
